import { useState } from 'react'
import { useI18n } from '../i18n/useI18n'

/**
 * Suppression en deux temps.
 *
 * Un premier appui montre la phrase qui dit exactement ce qui va
 * disparaitre, un second confirme. Aucune fenetre du navigateur : sur
 * un telephone, un confirm() natif se ferme d'un appui a cote et ne se
 * traduit pas.
 *
 * Le bouton de confirmation est le plus petit des deux : on ne rend
 * pas la destruction plus facile que l'annulation.
 */
export function ConfirmDelete({
  label,
  question,
  onDelete,
  disabled,
  blockedReason,
}: {
  label: string
  question: string
  onDelete: () => Promise<void>
  disabled?: boolean
  /** Si renseigne, la suppression est impossible et on dit pourquoi. */
  blockedReason?: string | null
}) {
  const { t } = useI18n()
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (blockedReason) {
    return (
      <div className="banner banner--warn">
        <span>{blockedReason}</span>
      </div>
    )
  }

  if (!asking) {
    return (
      <button
        type="button"
        className="btn btn--danger btn--small"
        disabled={disabled}
        onClick={() => setAsking(true)}
      >
        {label}
      </button>
    )
  }

  return (
    <div className="banner banner--error">
      <span>{question}</span>
      {error && <strong>{error}</strong>}
      <div className="banner__actions">
        <button
          type="button"
          className="btn btn--ghost btn--small"
          disabled={busy}
          onClick={() => {
            setAsking(false)
            setError(null)
          }}
        >
          {t('delete_cancel')}
        </button>
        <button
          type="button"
          className="btn btn--danger btn--small"
          disabled={busy}
          onClick={() => {
            setError(null)
            setBusy(true)
            void onDelete()
              .catch((e: unknown) => {
                setError(
                  `${t('delete_failed')} ${e instanceof Error ? e.message : String(e)}`,
                )
              })
              .finally(() => setBusy(false))
          }}
        >
          {busy ? t('deleting') : t('delete_confirm')}
        </button>
      </div>
    </div>
  )
}
